import { useStableCallback } from '@syconium/little-miss-figgy'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { reportClientError } from '../_components/chrome/scripts/DataDogRumScript'
import { useGraphqlClients } from '../_providers/GraphqlClientsProvider.client'
import { useTrackJoinedProductWaitlist } from '../_providers/TrackingProvider.client'
import { shortenShopifyId } from '../_utils/shortenShopifyId'

type VariantWaitlistResponse = 'success' | 'email-already-registered' | 'unexpected-error'

type JoinVariantWaitlistInput = {
  email: string
  variantShopifyId: string
  variantSku: string
}

export const useJoinVariantWaitlist = () => {
  const { personalizedClient } = useGraphqlClients()
  const { trackJoinedProductWaitlist: _trackJoinedProductWaitlist } =
    useTrackJoinedProductWaitlist()

  const trackJoinedProductWaitlist = useStableCallback((input: JoinVariantWaitlistInput) => {
    personalizedClient
      .query({
        query: gql(`
          query GetWaitlistSignalData($sku: String!) {
            productCatalogVariant(sku: $sku) {
              id
              sku
              shopifyId
              shopifyProductId
              size { id }
              fit { id }
              group {
                id
                color { id }
                product {
                  id
                  handle
                }
              }
            }
          }
    `),
        variables: {
          sku: input.variantSku,
        },
      })
      .then(response => {
        if (response.error || !response.data) {
          reportClientError({
            error: response.error,
            context: {
              scope: 'useWaitlistService GetWaitlistSignalData',
              label: 'Waitlist tracking data fetch failed',
            },
          })
        } else {
          _trackJoinedProductWaitlist({
            email: input.email,
            id: response.data.productCatalogVariant.group.id,
            handle: response.data.productCatalogVariant.group.product.handle,
            productShopifyId: response.data.productCatalogVariant.shopifyProductId,
            variantShopifyId: response.data.productCatalogVariant.shopifyId,
            variantSku: response.data.productCatalogVariant.sku,
            colorId: response.data.productCatalogVariant.group.color?.id,
            sizeId: response.data.productCatalogVariant.size?.id,
            fitId: response.data.productCatalogVariant.fit?.id,
          })
        }
      })
      .catch(error => {
        reportClientError({
          error,
          context: {
            scope: 'useWaitlistService GetWaitlistSignalData',
            label: 'Waitlist tracking data fetch failed',
          },
        })
      })
  })

  const joinVariantWaitlist = useStableCallback(async (input: JoinVariantWaitlistInput) => {
    try {
      const response = await fetch(`/api/waitlist/waitlists/product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: input.email,
          variantId: shortenShopifyId(input.variantShopifyId),
        }),
      })

      let waitlistRequestResult: VariantWaitlistResponse = 'success'

      if (response.status === 409) {
        // 409 just means that the user is already registered.
        waitlistRequestResult = 'email-already-registered'
      } else if (!response.ok) {
        waitlistRequestResult = 'unexpected-error'
        reportClientError({
          error: new Error('Waitlist request failed'),
          context: {
            scope: 'useWaitlistService sendWaitlistRequest',
            label: '!response.ok',
          },
        })
      }

      if (waitlistRequestResult === 'success') {
        trackJoinedProductWaitlist(input)
      }

      return waitlistRequestResult
    } catch (error) {
      reportClientError({
        error,
        context: {
          scope: 'useWaitlistService sendWaitlistRequest',
          label: 'Waitlist request failed',
        },
      })

      return 'unexpected-error'
    }
  })

  return {
    joinVariantWaitlist,
  }
}
